import { RootState } from "@src/redux/store";
import { LoggerContext, RepositoryContext } from "@src/shared/contexts";
import _ from "lodash";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { ErrorToast } from "../toasts/ErrorToast";
import { SuccessToast } from "../toasts/SuccessToast";
import { setIsLoading } from "@src/redux/feature/modalHandler";
import { useContext, useEffect } from "react";
import { useErrorBoundary } from "react-error-boundary";

const EditProfileModal = ({ closeEditProfileModal }: EditProfileModalProps) => {
  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    setValue,
    formState: { errors },
  } = useForm<ProfileData>();

  const isLoading = useSelector(
    (state: RootState) => state.modalHandler.isLoading
  );
  const ProfileData = useSelector(
    (state: RootState) => state.profileSlice.user
  );
  const dispatch = useDispatch();
  const repository = useContext(RepositoryContext);
  const profileRepo = repository.profile;
  const { showBoundary } = useErrorBoundary();
  const customLogger = useContext(LoggerContext);

  useEffect(() => {
    clearErrors();
    setValue("first_name", ProfileData?.first_name!);
    setValue("last_name", ProfileData?.last_name!);
  }, [setValue, clearErrors]);

  const onSubmit = async (data: ProfileData) => {
    dispatch(setIsLoading(true));
    try {
      const response = await profileRepo.editProfile<ProfileData, ProfileData>(
        data
      );
      if (!_.isUndefined(response)) {
        if ("errors" in response) {
          _.map(response.errors, (error) => {
            const validField = error.title as "first_name" | "last_name";
            setError(validField, { message: error.message });
            if (validField !== "first_name" && validField !== "last_name") {
              ErrorToast({ Message: error.message });
              clearErrors();
            }
          });
          await customLogger.reportErrorResponse(showBoundary, response);
        } else {
          closeEditProfileModal?.();
          const info: InfoLog = {
            message: "Account details updated.",
          };
          await customLogger.log(info);
          SuccessToast({
            Message: "Success! Your Account info has been Edited.",
          });
        }
        dispatch(setIsLoading(false));
      }
    } catch (error) {
      showBoundary(error);
      await customLogger.reportError(error);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[2]">
      <div className="bg-white rounded-md z-[2] p-[20px] md:px-[32px] w-[354px] md:w-[850px]">
        <form onSubmit={handleSubmit(onSubmit)}>
          <h2 className="text-[28px] font-[700] text-text_black">
            Edit Account Settings
          </h2>
          <div className="mt-2">
            <label
              className="text-text_gray text-sm font-[500]"
              htmlFor="first_names"
            >
              First Name
              <span className="text-red text-sm ml-1">*</span>
            </label>
            <input
              {...register("first_name", {
                required: "The first name field is required",
              })}
              className="border border-border_light_gray rounded-[10px] w-full h-[48px]  py-2 px-3 text-gray-700 focus:outline-none mt-[10px]"
              type="text"
              placeholder="First Name"
            />
            {errors.first_name != null && (
              <small className="block text-red mt-2">
                {errors.first_name.message}
              </small>
            )}
          </div>
          <div className="mt-2">
            <label
              className="text-text_gray text-sm font-[500]"
              htmlFor="last_name"
            >
              Last Name<span className="text-red text-sm ml-1">*</span>
            </label>
            <input
              {...register("last_name", {
                required: "The last name field is required",
              })}
              className="border border-border_light_gray rounded-[10px] w-full h-[48px]  py-2 px-3 text-gray-700 focus:outline-none mt-[10px]"
              type="text"
              placeholder="Last Name"
            />
            {errors.last_name != null && (
              <small className="block text-red mt-2">
                {errors.last_name.message}
              </small>
            )}
          </div>
          <div className="flex justify-start gap-[20px] mt-[28px]">
            {isLoading ? (
              <button
                disabled
                className="flex items-center gap-2 px-5 bg-button_blue bg-opacity-[0.70] w-[160px] h-[40px] text-white text-[14px] font-[700] rounded-md"
              >
                <img
                  width={22}
                  height={22}
                  src="/icons/Loading.svg"
                  alt="loading"
                />
                Creating...
              </button>
            ) : (
              <button
                type="submit"
                className="bg-button_blue w-[160px] h-[40px] text-white text-[14px] font-[700] rounded-md"
              >
                Save and Proceed
              </button>
            )}
            <button
              type="button"
              onClick={closeEditProfileModal}
              className="border border-button_blue w-[90px] h-[40px] text-nav_blue text-[14px] font-[500] rounded-md"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfileModal;
